import { BookOpen, Brain, Target, Lightbulb, MessageCircle, Users, Award } from 'lucide-react';

// 数据类型定义
export interface StatItem {
  label: string;
  value: string;
  icon: string;
  color: string;
}

export interface FeatureItem {
  icon: string;
  title: string;
  description: string;
  href: string;
  color: string;
  isNew?: boolean;
  comingSoon?: boolean;
}

export interface ProcessStep {
  stepNumber: number;
  title: string;
  description: string;
  color: string;
}

export interface Testimonial {
  name: string;
  role: string;
  content: string;
  rating: number;
  avatar?: string;
}

export interface TrustIndicator {
  label: string;
  description: string;
}

export interface HeroConfig {
  headline: string;
  subheadline: string;
  primaryCTA: string;
  secondaryCTA: string;
  aiLabel: string;
}

export interface HomepageConfig {
  hero: HeroConfig;
  stats: StatItem[];
  features: FeatureItem[];
  process: ProcessStep[];
  testimonials: Testimonial[];
  trustIndicators: TrustIndicator[];
}

// 首页配置数据
export const homepageConfig: HomepageConfig = {
  hero: {
    headline: '掌握你的思维技能',
    subheadline: '通过科学的训练方法和AI辅助，系统性地提升逻辑思维、创意思维和表达能力。获得即时反馈，让你的思维能力快速提升。',
    primaryCTA: '开始免费体验',
    secondaryCTA: '观看2分钟演示',
    aiLabel: 'AI驱动的思维能力训练',
  },

  stats: [
    {
      label: '思维模型',
      value: '50+',
      icon: 'Brain',
      color: 'bg-blue-500',
    },
    {
      label: '训练题目',
      value: '200+',
      icon: 'Target',
      color: 'bg-purple-500',
    },
    {
      label: '学习用户',
      value: '1000+',
      icon: 'Users',
      color: 'bg-green-500',
    },
    {
      label: '完成率',
      value: '95%',
      icon: 'Award',
      color: 'bg-orange-500',
    },
  ],

  features: [
    {
      icon: 'Target',
      title: '思维训练',
      description: '基于认知科学的渐进式训练体系，从逻辑推理到创意思考，AI实时评估你的思维过程，提供个性化改进建议，让思维能力看得见的提升',
      href: '/models',
      color: 'bg-green-500',
    },
    {
      icon: 'BookOpen',
      title: '模型工厂',
      description: '掌握50+经典思维模型，从麦肯锡金字塔到SCAMPER创新法，每个模型都有详细案例解析、实战练习和AI个性化指导，让抽象概念变成实用工具',
      href: '/models',
      color: 'bg-blue-500',
    },
    {
      icon: 'Brain',
      title: '案例精选',
      description: '精选真实商业案例和社会热点，专业分析师深度解读，学习顶级思维过程，提升分析判断能力，每周更新3-5篇高质量分析',
      href: '/analyses',
      color: 'bg-purple-500',
    },
    {
      icon: 'Lightbulb',
      title: '问题诊断系统',
      description: '独创四系统视角（个人、人际、组织、社会）+ 五大诊断原理，像医生诊断疾病一样系统分析复杂问题，找到根本原因和最优解决方案',
      href: '/problem-diagnosis',
      color: 'bg-yellow-500',
      isNew: true,
      comingSoon: true,
    },
    {
      icon: 'MessageCircle',
      title: 'AI思维导师',
      description: '24小时在线的专业思维教练，基于你的学习进度和思维特点，提供定制化指导，解答疑惑，推荐学习路径，是你的专属思维提升顾问',
      href: '/ai-assistant',
      color: 'bg-indigo-500',
      comingSoon: true,
    },
    {
      icon: 'Users',
      title: '学习社区',
      description: '与志同道合的学习者交流思维心得，参与案例讨论，分享学习笔记，在思维碰撞中获得新的启发和成长动力',
      href: '/community',
      color: 'bg-pink-500',
      isNew: true,
      comingSoon: true,
    },
  ],

  process: [
    {
      stepNumber: 1,
      title: '学习模型',
      description: '在模型工厂中学习各种思维模型，记录笔记，与AI交流',
      color: 'bg-blue-500',
    },
    {
      stepNumber: 2,
      title: '实践训练',
      description: '通过案例分析练习进行实际训练，获得AI评价和建议',
      color: 'bg-purple-500',
    },
    {
      stepNumber: 3,
      title: '持续提升',
      description: '查看学习记录，跟踪进度，持续提升思维能力',
      color: 'bg-green-500',
    },
  ],

  testimonials: [
    {
      name: '张明',
      role: '产品经理',
      content: '通过LeapWard平台，我的逻辑思维能力得到了显著提升。AI反馈非常及时和准确，帮助我在工作中做出更好的决策。',
      rating: 5,
    },
    {
      name: '李华',
      role: '大学生',
      content: '模型工厂里的思维模型非常实用，每个模型都有详细的解释和练习。现在我在学习和思考问题时更有条理了。',
      rating: 5,
    },
    {
      name: '王芳',
      role: '创业者',
      content: '问题诊断功能帮助我系统性地分析商业问题。四系统视角让我能从多个角度思考，避免了很多决策盲点。',
      rating: 5,
    },
    {
      name: '陈强',
      role: '软件工程师',
      content: '热点演练让我在面对复杂技术问题时思路更清晰。AI助手的建议总是很有针对性，帮助我快速找到解决方案。',
      rating: 5,
    },
  ],

  trustIndicators: [
    {
      label: 'SSL安全认证',
      description: '数据传输加密保护',
    },
    {
      label: '专业认证',
      description: '深度分析专业认证',
    },
    {
      label: '用户信赖',
      description: '95%用户满意度',
    },
  ],
};

// 图标映射
export const iconMap = {
  BookOpen,
  Brain,
  Target,
  Lightbulb,
  MessageCircle,
  Users,
  Award,
};

// 获取图标组件
export const getIconComponent = (iconName: string) => {
  return iconMap[iconName as keyof typeof iconMap];
};